import { careerData, type CareerStep } from './aboutData';
import type { ExperienceTile, PerspectiveFace } from './perspectiveData';

// Map development areas to perspective faces
const AREA_MAPPING: Record<string, PerspectiveFace['id']> = {
    products: 'products',
    people: 'people',
    organizations: 'organizations',
    competences: 'competences',
    personal: 'family',
    community: 'community'
};

// Map career categories to icons
const getCategoryIcon = (category: string): string => {
    switch (category) {
        case 'education':
            return 'GraduationCap';
        case 'work':
            return 'Briefcase';
        case 'personal':
            return 'Heart';
        case 'publication':
            return 'BookOpen';
        default:
            return 'Circle';
    }
};

// Convert a CareerStep to an ExperienceTile
const careerStepToTile = (step: CareerStep): ExperienceTile => {
    const projects = Array.isArray(step.projects) ? step.projects : [];
    const projectStrings = projects.map(p => typeof p === 'string' ? p : p.name);

    return {
        id: step.id,
        title: step.role,
        subtitle: step.company,
        period: step.period,
        icon: getCategoryIcon(step.category),
        summary: step.description,
        type: step.imageUrl ? 'image' : 'text',
        imageUrl: step.imageUrl,
        detailedView: {
            description: step.description,
            achievements: projectStrings,
            skills: step.developmentArea || [],
            connections: []
        },
        span: step.tileSpan
    };
};

// Group career data by development areas
export const generatePerspectiveTiles = (data: CareerStep[] = careerData): Record<string, ExperienceTile[]> => {
    const perspectiveTiles: Record<string, ExperienceTile[]> = {
        products: [],
        people: [],
        organizations: [],
        competences: [],
        family: [],
        community: []
    };

    // Keep track of added IDs per category to ensure uniqueness in O(1)
    const addedIds = {
        products: new Set<string>(),
        people: new Set<string>(),
        organizations: new Set<string>(),
        competences: new Set<string>(),
        family: new Set<string>(),
        community: new Set<string>()
    };

    data.forEach(step => {
        const tile = careerStepToTile(step);

        // Map development areas to perspective faces
        if (step.developmentArea && step.developmentArea.length > 0) {
            step.developmentArea.forEach(area => {
                const key = AREA_MAPPING[area];

                if (key && !addedIds[key].has(tile.id)) {
                    perspectiveTiles[key].push(tile);
                    addedIds[key].add(tile.id);
                }
            });
        } else {
            // If no development area specified, categorize by category
            if (step.category === 'personal') {
                perspectiveTiles.family.push(tile);
            } else if (step.category === 'education') {
                perspectiveTiles.competences.push(tile);
            } else if (step.category === 'publication') {
                perspectiveTiles.community.push(tile);
            }
        }
    });

    // Sort each perspective by period (earliest first for top-left to bottom-right chronology)
    Object.keys(perspectiveTiles).forEach(key => {
        perspectiveTiles[key].sort((a, b) => {
            // Extract year from period string (handle ranges like "2020-2024")
            const getYear = (period: string) => {
                const match = period.match(/(\d{4})/);
                return match ? parseInt(match[1]) : 0;
            };
            return getYear(a.period) - getYear(b.period); // Earliest first
        });
    });

    return perspectiveTiles;
};

// Generate the complete perspective faces with career data
export const generatePerspectiveFaces = (data: CareerStep[] = careerData): PerspectiveFace[] => {
    const tiles = generatePerspectiveTiles(data);

    return [
        {
            id: 'products',
            title: 'Developing Products',
            subtitle: 'Teaches the perspective of customers, business needs, and technical feasibility, balancing what users want with what\'s viable and valuable.',
            color: '#06B6D4',
            description: 'From software engineering to ML/AI systems',
            tiles: tiles.products.slice(0, 9) // Limit to 9 tiles per face
        },
        {
            id: 'people',
            title: 'Developing People',
            subtitle: 'You can\'t develop people, but we can help each other to develop ourselves - on eye level and the freedom of choice.',
            color: '#8B5CF6',
            description: 'Coaching, mentoring, and leadership',
            tiles: tiles.people.slice(0, 9)
        },
        {
            id: 'organizations',
            title: 'Developing Organizations',
            subtitle: 'It\'s not about perfecting structure or culture alone, but aligning both to create adaptive systems where people and processes evolve together.',
            color: '#EC4899',
            description: 'Agile transformation and organizational development',
            tiles: tiles.organizations.slice(0, 9)
        },
        {
            id: 'competences',
            title: 'Developing Competences',
            subtitle: 'It\'s not about who knows more, but who is brave enough to try, figure out what competences were missing, learning them and doing it again.',
            color: '#F59E0B',
            description: 'Education, thought leadership, and teaching',
            tiles: tiles.competences.slice(0, 9)
        },
        {
            id: 'family',
            title: 'Developing Family',
            subtitle: 'Family teaches us to see with the eyes of others, hear with the ears of others, and feel with the hearts of others.',
            color: '#10B981',
            description: 'Personal life, family moments, and work-life balance',
            isPrivate: true,
            tiles: tiles.family.slice(0, 9)
        },
        {
            id: 'community',
            title: 'Developing Community',
            subtitle: 'There is a reason why research is so fascinated by communities: How do you motivate people without paying them? Short answer: Don\'t motivate people.',
            color: '#6366F1',
            description: 'Basketball, mentoring, and community involvement',
            tiles: tiles.community.slice(0, 9)
        }
    ];
};
